import React, { useContext, useState, useEffect, useMemo } from 'react';
import './Payment.css';
import { PaymentContext } from "../../context/PaymentContext.js";
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import Loader from '../../components/Loader.js';
import { useToast } from '../../context/ToastContext.js';
import { AuthContext } from '../../context/AuthContext.js';
import {
  validateCardForm,
  formatExpiryDate,
  validateTCKN
} from '../../lib/validators.js';
import countryCodesData from '../../constants/countryCodes.json';
import CountrySelector from '../../components/CountrySelector';
import { getApiUrl } from '../../config';

Modal.setAppElement('#root');

const Payment = () => {
  const {
    state: { cardNumber, totalAmount, cardHolderName, expiryDate, cvc, installments, commissionRate, selectedInstallment, error, paymentSuccess },
    setCardNumber,
    setCardHolderName,
    setExpiryDate,
    setCvc,
    setTotalAmount,
    fetchCommissionRates,
    fetchInstallments,
    setSelectedInstallment,
    processPayment,
    calculateMonthlyPayment
  } = useContext(PaymentContext);

  const location = useLocation();
  const { total, customerForm: initialCustomerForm, cart } = location.state || {};
  const [customerForm, setCustomerForm] = useState(initialCustomerForm || {});
  const [isOpen, setIsOpen] = useState(false);
  const [isFormActive, setIsFormActive] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedInstallmentData, setSelectedInstallmentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Edit mode states
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [editedInvoiceData, setEditedInvoiceData] = useState({});

  // Country selector state
  const countryCodes = useMemo(() => {
    const turkey = countryCodesData.find(c => c.code === 'TR');
    const others = countryCodesData.filter(c => c.code !== 'TR').sort((a, b) => a.name.localeCompare(b.name));
    return [turkey, ...others];
  }, []);

  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [isCvcFocused, setIsCvcFocused] = useState(false);

  const authContext = useContext(AuthContext);
  const isLoggedIn = useMemo(() => {
    const contextToken = authContext?.token;
    return Boolean(contextToken || localStorage.getItem('token'));
  }, [authContext?.token]);

  // Kullanıcı bakiyesi ve ödeme dağılımı state'leri
  const [balanceInfo, setBalanceInfo] = useState(null);
  const [paymentDistribution, setPaymentDistribution] = useState(null);

  const invoiceFieldMapping = useMemo(() => ({
    ad: 'faturaAd',
    soyad: 'faturaSoyad',
    tckimlik: 'faturaTckimlik',
    telefon: 'faturaTelefon',
    dogumTarihi: 'faturaDogumTarihi',
    adres: 'faturaAdres',
    email: 'faturaEmail'
  }), []);

  // Sarrafiye ürünleri kontrolü
  const hasSarrafiyeProducts = cart && cart.some(product => product.sarrafiye == 1);

  const formatPhoneForDisplay = (number = '') => {
    const digits = number.replace(/\D/g, '');
    if (digits.length !== 11) return digits || '-';
    return digits.replace(/(\d{4})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  };

  const formatDateForDisplay = (value = '') => {
    if (!value) return '-';

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return `${day}.${month}.${year}`;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      const day = `${parsed.getDate()}`.padStart(2, '0');
      const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
      const year = `${parsed.getFullYear()}`;
      return `${day}.${month}.${year}`;
    }

    return value;
  };

  const billingInfo = useMemo(() => {
    if (!customerForm) return {};

    const billingAddress =
      customerForm.faturaAdresi ||
      (customerForm.faturamAyniAdres ? customerForm.teslimatAdresi : null);

    const resolvedAddress = billingAddress || customerForm.teslimatAdresi || {};

    return {
      ad: customerForm.faturaAd ?? resolvedAddress.ad ?? customerForm.ad ?? '',
      soyad: customerForm.faturaSoyad ?? resolvedAddress.soyad ?? customerForm.soyad ?? '',
      tckimlik: customerForm.faturaTckimlik ?? resolvedAddress.tc ?? '',
      telefon: customerForm.faturaTelefon ?? resolvedAddress.telefon ?? customerForm.telefon ?? '',
      dogumTarihi: customerForm.faturaDogumTarihi ?? resolvedAddress.dogum_tarihi ?? '',
      adres: customerForm.faturaAdres ?? resolvedAddress.addressText ?? customerForm.adres ?? '',
      il: customerForm.faturaIl ?? resolvedAddress.il ?? customerForm.il ?? '',
      ilce: customerForm.faturaIlce ?? resolvedAddress.ilce ?? customerForm.ilce ?? '',
      email: customerForm.faturaEmail ?? resolvedAddress.email ?? customerForm.email ?? ''
    };
  }, [customerForm]);

  const billingAddressLine = useMemo(() => {
    const parts = [];
    if (billingInfo.adres) {
      parts.push(billingInfo.adres);
    }
    const cityParts = [billingInfo.ilce, billingInfo.il].filter(Boolean).join(', ');
    if (cityParts) {
      parts.push(cityParts);
    }
    return parts.join(' • ');
  }, [billingInfo.adres, billingInfo.il, billingInfo.ilce]);

  const handleFocus = () => setIsFormActive(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (total) {
      setTotalAmount(total);

      // Kullanıcı giriş yapmışsa bakiye bilgisini al
      if (isLoggedIn) {
        fetchBalanceAndDistribution(total);
      }
    }
    fetchCommissionRates();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsEditingInvoice(true);
      setEditedInvoiceData(prev => {
        if (Object.keys(prev || {}).length > 0) {
          return prev;
        }
        return {
          ad: '',
          soyad: '',
          tckimlik: '',
          telefon: '',
          dogumTarihi: '',
          adres: '',
          email: ''
        };
      });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (cardNumber.length == 8 || cardNumber.length == 16) {
      fetchInstallments(cardNumber);
    } else {
      setSelectedInstallment(null);
    }
  }, [cardNumber]);

  useEffect(() => {
    const fullName = `${billingInfo.ad || ''} ${billingInfo.soyad || ''}`.trim();
    if (fullName && fullName !== cardHolderName) {
      setCardHolderName(fullName);
    }
  }, [billingInfo.ad, billingInfo.soyad, cardHolderName, setCardHolderName]);

  // Taksit seçenekleri yüklenmediyse varsayılan 1 taksit seç
  useEffect(() => {
    if (cardNumber.length >= 8 && (!installments || installments.length === 0) && !selectedInstallment) {
      setSelectedInstallment(1);
      setSelectedInstallmentData({ count: 1, rate: 0, totalAmount: total });
    }
  }, [cardNumber, installments, selectedInstallment, total]);

  const validateForm = () => {
    return validateCardForm({
      cardNumber,
      cardHolderName,
      expiryDate,
      cvc,
      selectedInstallmentData
    });
  };

  const handleCardNumberChange = (e) => {
    let newCardNumber = e.target.value.replace(/\D/g, '');
    if (newCardNumber.length > 0) {
      newCardNumber = newCardNumber.slice(0, 16);
    }
    setCardNumber(newCardNumber);
    setErrors(prev => ({ ...prev, cardNumber: '' }));
  };

  const handleInstallmentChange = (e) => {
    const selectedValue = Number(e.target.value);
    setSelectedInstallment(selectedValue);
    const selected = installments.find(inst => inst.count === selectedValue);
    setSelectedInstallmentData(selected);
    setErrors(prev => ({ ...prev, installment: '' }));
  };

  const handleCardHolderNameChange = (e) => {
    setCardHolderName(e.target.value);
    setErrors(prev => ({ ...prev, cardHolderName: '' }));
  };

  const handleExpiryDateChange = (e) => {
    const formattedDate = formatExpiryDate(e.target.value);
    setExpiryDate(formattedDate);
    setErrors(prev => ({ ...prev, expiryDate: '' }));
  };

  useEffect(() => {
    if (total) {
      setTotalAmount(total);
    }
  }, [total]);

  const handleCvcChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 3);
    setCvc(value);
    setErrors(prev => ({ ...prev, cvc: '' }));
  };

  // Bakiye ve ödeme dağılımını getir
  const fetchBalanceAndDistribution = async (amount) => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');

      const balanceResponse = await fetch(`${getApiUrl()}/calculate_balance_usage`, {
        method: 'POST',
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: amount })
      });

      const balanceData = await balanceResponse.json();
      if (balanceData.success) {
        setBalanceInfo(balanceData);
        setPaymentDistribution(balanceData.will_use);
      }
    } catch (error) {
      console.error('Bakiye bilgisi alınırken hata:', error);
    }
  };

  // Fatura Düzenleme Fonksiyonları
  const handleEditClick = () => {
    setEditedInvoiceData({
      ad: billingInfo.ad,
      soyad: billingInfo.soyad,
      tckimlik: billingInfo.tckimlik,
      telefon: billingInfo.telefon,
      dogumTarihi: billingInfo.dogumTarihi,
      adres: billingInfo.adres,
      email: billingInfo.email
    });
    setIsEditingInvoice(true);
  };

  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;

    setEditedInvoiceData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'ad' || name === 'soyad') {
      const newAd = name === 'ad' ? value : editedInvoiceData.ad || '';
      const newSoyad = name === 'soyad' ? value : editedInvoiceData.soyad || '';
      const fullName = `${newAd} ${newSoyad}`.trim();
      setCardHolderName(fullName);
    }

    const mappedKey = invoiceFieldMapping[name];
    if (mappedKey) {
      setCustomerForm(prev => ({
        ...(prev || {}),
        [mappedKey]: value
      }));
    }
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const maxLength = selectedCountry.mobile_number_length || 15;

    if (sanitizedValue.length > maxLength) {
      return;
    }

    setEditedInvoiceData(prev => ({
      ...prev,
      telefon: sanitizedValue
    }));

    const mappedKey = invoiceFieldMapping['telefon'];
    if (mappedKey) {
      setCustomerForm(prev => ({
        ...(prev || {}),
        [mappedKey]: sanitizedValue
      }));
    }
  };

  const handleSaveInvoice = () => {
    if (!editedInvoiceData.ad || !editedInvoiceData.soyad || !editedInvoiceData.tckimlik || !editedInvoiceData.telefon || !editedInvoiceData.adres) {
      showToast('Lütfen zorunlu alanları doldurunuz.', 'error');
      return;
    }

    if (!validateTCKN(editedInvoiceData.tckimlik)) {
      showToast('Geçersiz TC Kimlik Numarası.', 'error');
      return;
    }

    setCustomerForm(prev => ({
      ...prev,
      faturaAd: editedInvoiceData.ad,
      faturaSoyad: editedInvoiceData.soyad,
      faturaTckimlik: editedInvoiceData.tckimlik,
      faturaTelefon: editedInvoiceData.telefon,
      faturaDogumTarihi: editedInvoiceData.dogumTarihi,
      faturaAdres: editedInvoiceData.adres,
      faturaEmail: editedInvoiceData.email
    }));
    setIsEditingInvoice(false);
    showToast('Fatura bilgileri güncellendi.', 'success');
  };

  const handleCancelEdit = () => {
    setIsEditingInvoice(false);
    setEditedInvoiceData({});
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    const isFullyPaidByBalance = balanceInfo && balanceInfo.balance_covers_full_amount;

    if (!isFullyPaidByBalance && !selectedInstallment) {
      if (!installments || installments.length === 0) {
        setSelectedInstallment(1);
        setSelectedInstallmentData({ count: 1, rate: 0, totalAmount: total });
      } else {
        setErrors(prev => ({ ...prev, submit: 'Taksit seçmediniz. Lütfen taksit seçeneğini belirtiniz.' }));
        showToast('Lütfen taksit seçeneğini belirtiniz.', 'error');
        return;
      }
    }

    if (!isFullyPaidByBalance) {
      const formErrors = validateForm();
      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);

        if (formErrors.cardNumber) {
          showToast(`Lütfen ${formErrors.cardNumber}`, 'error');
        }
        if (formErrors.cardHolderName) {
          showToast(`Lütfen ${formErrors.cardHolderName}`, 'error');
        }
        if (formErrors.expiryDate) {
          showToast(`Lütfen ${formErrors.expiryDate}`, 'error');
        }
        if (formErrors.cvc) {
          showToast(`Lütfen ${formErrors.cvc}`, 'error');
        }
        if (formErrors.installment) {
          showToast(`Lütfen ${formErrors.installment}`, 'error');
        }

        return;
      }
    }

    setLoading(true);
    try {
      const referansKodu = location.state?.referansKodu || '';

      if (!customerForm) {
        showToast('Fatura bilgileri bulunamadı. Lütfen adres adımına geri dönün.', 'error');
        setLoading(false);
        return;
      }

      if (
        !billingInfo.ad ||
        !billingInfo.soyad ||
        !billingInfo.adres ||
        !billingInfo.dogumTarihi ||
        !billingInfo.tckimlik ||
        !billingInfo.telefon
      ) {
        showToast('Fatura bilgileri eksik. Lütfen bilgileri düzenleyiniz.', 'error');
        setLoading(false);
        return;
      }

      const tcknDigits = billingInfo.tckimlik.replace(/\D/g, '');
      const phoneDigits = billingInfo.telefon.replace(/\D/g, '');
      const expectedLength = selectedCountry.mobile_number_length;

      if (!validateTCKN(tcknDigits)) {
        showToast('Fatura TC Kimlik bilgisinde sorun var. Lütfen kontrol ediniz.', 'error');
        setLoading(false);
        return;
      }

      if (phoneDigits.length !== expectedLength) {
        showToast(`Telefon numarası ${expectedLength} haneli olmalıdır. Lütfen kontrol ediniz.`, 'error');
        setLoading(false);
        return;
      }

      const updatedCustomerForm = {
        ...customerForm,
        faturaAd: (billingInfo.ad || '').trim(),
        faturaSoyad: (billingInfo.soyad || '').trim(),
        faturaAdres: (billingInfo.adres || '').trim(),
        faturaDogumTarihi: billingInfo.dogumTarihi,
        faturaTckimlik: tcknDigits,
        faturaTelefon: phoneDigits,
        faturaIl: billingInfo.il ?? '',
        faturaIlce: billingInfo.ilce ?? '',
        faturaEmail: billingInfo.email ?? ''
      };

      const installmentData = selectedInstallmentData || { count: 1, rate: 0 };

      let finalAmount;
      if (hasSarrafiyeProducts && installmentData.count === 1) {
        const sarrafiyeCommission = total * 0.04;
        finalAmount = total + sarrafiyeCommission;
      } else {
        finalAmount = total * (1 + (installmentData.rate / 100));
      }

      const paymentData = {
        kart_numarasi: cardNumber,
        isim: cardHolderName,
        son_kullanma: expiryDate,
        ccv: cvc,
        taksit_sayisi: installmentData.count,
        odenecek_tutar: finalAmount,
        musteriForm: updatedCustomerForm,
        referans_kodu: referansKodu,
      };

      fbq('track', 'Purchase', {
        currency: 'TRY',
        value: total
      });

      const response = await processPayment(paymentData, showToast);

      if (response.success) {
        document.cookie = "share_ref=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        if (!response.redirect_url) {
          navigate('/payment-result/success', {
            state: {
              orderId: response.orderId,
              amount: response.price || 0
            }
          });
          return;
        }

        if (response.redirect_url) {
          window.location.replace(response.redirect_url);
          return;
        }
      } else {
        throw new Error(response.message || 'Ödeme işlemi başarısız');
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Ödeme işlemi sırasında bir hata oluştu'
      }));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className='loader-info'>
    <p className='m-0'>İşlem alınıyor...</p>
    <Loader />
  </div>;

  return (
    <>
      <div className='payment-title'>
        <h2 className='my-3'>Kredi Kartı ile Ödeme</h2>

        <div>
          <div>
            <div className="payment-information-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Fatura Bilgileri</h3>
              {isLoggedIn && !isEditingInvoice && (
                <button
                  onClick={handleEditClick}
                  style={{
                    padding: '5px 10px',
                    fontSize: '0.9rem',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Düzenle
                </button>
              )}
            </div>
            <div>
              <p className="invoice-info-text"><strong>Fatura bilgileri</strong> kredi kartı sahibi ile <strong>aynı olmalıdır</strong></p>
              <p className='invoice-info-text'>Son ekrana gelene kadar ürün fiyatları kurlar ile birlikte <strong>güncellenmektedir.</strong> Son ulaştığınız ekrandaki fiyat geçerlidir.</p>
            </div>
          </div>
          <div className="invoice-information">
            <div className="invoice-form p-md-4 p-2">
              {isEditingInvoice ? (
                <>
                  <div className="d-flex flex-md-row flex-column gap-2">
                    <div className="form-group">
                      <label>Ad</label>
                      <input
                        type="text"
                        name="ad"
                        value={editedInvoiceData.ad || ''}
                        onChange={handleInvoiceChange}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Soyad</label>
                      <input
                        type="text"
                        name="soyad"
                        value={editedInvoiceData.soyad || ''}
                        onChange={handleInvoiceChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="d-flex flex-md-row flex-column gap-2">
                    <div className='form-group'>
                      <label>TC Kimlik No</label>
                      <input
                        type="text"
                        name="tckimlik"
                        value={editedInvoiceData.tckimlik || ''}
                        onChange={handleInvoiceChange}
                        maxLength="11"
                        className="form-control"
                        style={{ marginBottom: '5px' }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Telefon</label>
                      <div className='d-flex gap-2'>
                        <div>
                          <CountrySelector
                            selectedCountry={selectedCountry}
                            onSelect={(country) => {
                              setSelectedCountry(country);
                              setEditedInvoiceData(prev => ({ ...prev, telefon: '' }));
                            }}
                            countries={countryCodes}
                          />
                        </div>
                        <input
                          type="tel"
                          name="telefon"
                          value={editedInvoiceData.telefon || ''}
                          onChange={handlePhoneChange}
                          className="form-control"
                          placeholder={selectedCountry.mobile_number_length ? `${selectedCountry.mobile_number_length} haneli` : "Telefon"}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='d-flex flex-md-row flex-column gap-2'>
                    <div className="form-group">
                      <label>Doğum Tarihi</label>
                      <input
                        type="date"
                        name="dogumTarihi"
                        value={editedInvoiceData.dogumTarihi || ''}
                        onChange={handleInvoiceChange}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={editedInvoiceData.email || ''}
                        onChange={handleInvoiceChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Adres</label>
                    <textarea
                      name="adres"
                      value={editedInvoiceData.adres || ''}
                      onChange={handleInvoiceChange}
                      className="form-control"
                      rows="3"
                    />
                  </div>
                  {isLoggedIn && (
                    <div className="d-flex gap-2 justify-content-end">
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="btn btn-secondary"
                      >
                        İptal
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveInvoice}
                        className="btn btn-secondary"
                      >
                        Kaydet
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="d-flex flex-md-row flex-column gap-2">
                    <div className="form-group">
                      <label>Ad</label>
                      <p className="readonly-text">
                        {`${billingInfo.ad || ''}`.trim() || '-'}
                      </p>
                    </div>
                    <div className="form-group">
                      <label>Soyad</label>
                      <p className="readonly-text">
                        {`${billingInfo.soyad || ''}`.trim() || '-'}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex flex-md-row flex-column gap-2">
                    <div className="form-group">
                      <label>TC Kimlik No</label>
                      <p className="readonly-text">
                        {billingInfo.tckimlik || '-'}
                      </p>
                    </div>
                    <div className="form-group">
                      <label>Telefon</label>
                      <p className="readonly-text">
                        {formatPhoneForDisplay(billingInfo.telefon)}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex flex-md-row flex-column gap-2">
                    <div className="form-group">
                      <label>Doğum Tarihi</label>
                      <p className="readonly-text">
                        {formatDateForDisplay(billingInfo.dogumTarihi)}
                      </p>
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <p className="readonly-text">{billingInfo.email || '-'}</p>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Adres</label>
                    <p className="readonly-text">
                      {billingAddressLine || '-'}
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className={`payment-page ${isFormActive ? "form-active" : ""}`}>
              <div className="payment-container">
                <div className="payment-form">
                  <form onSubmit={handlePaymentSubmit}>
                    {balanceInfo && balanceInfo.balance_covers_full_amount ? (
                      <div className="full-balance-payment-notice" style={{
                        backgroundColor: '#e8f5e9',
                        border: '2px solid #4caf50',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        marginBottom: '20px'
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '15px' }}>✅</div>
                        <h3 style={{ color: '#2e7d32', margin: '0 0 10px 0' }}>
                          Siparişiniz Tamamen Bakiyenizle Ödenecektir
                        </h3>
                        <p style={{ margin: '10px 0', fontSize: '14px', color: '#1b5e20' }}>
                          Toplam Tutar: <strong>{total.toFixed(2)} TL</strong><br />
                          Bakiye Kullanımı: <strong>{balanceInfo.will_use.total.toFixed(2)} TL</strong><br />
                          Kart ile Ödenecek: <strong>0.00 TL</strong>
                        </p>
                        <p style={{ margin: '15px 0 0 0', fontSize: '13px', color: '#424242' }}>
                          Kart bilgisi girmenize gerek yoktur. Aşağıdaki "Ödeme Yap" butonuna tıklayarak siparişinizi tamamlayabilirsiniz.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="commission-info">
                          <button type="button" onClick={() => setIsOpen(true)}>Komisyon Oranları</button>
                          {commissionRate.length > 0 && (
                            <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)} contentLabel="Komisyon Oranları" className="ReactModal__Content"
                              overlayClassName="ReactModal__Overlay">
                              <h2>Komisyon Oranları</h2>
                              <button className="close-button" onClick={() => setIsOpen(false)}>
                                &times;</button>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Banka</th>
                                    <th>1 Taksit</th>
                                    <th>2 Taksit</th>
                                    <th>3 Taksit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {commissionRate.map((bank, i) => {
                                    const taksit1 = bank.taksit.find((t) => t.count == 1)?.rate ?? '';
                                    let taksit2 = bank.taksit.find((t) => t.count == 2)?.rate ?? '';
                                    let taksit3 = bank.taksit.find((t) => t.count == 3)?.rate ?? '';
                                    taksit2 = parseFloat(taksit2).toFixed(2)
                                    taksit3 = parseFloat(taksit3).toFixed(2)

                                    return (
                                      <tr key={i}>
                                        <td>{bank.name}</td>
                                        <td>{taksit1}</td>
                                        <td>{taksit2}%</td>
                                        <td>{taksit3}%</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </Modal>
                          )}
                        </div>

                        {/* Bakiye Kullanım Bilgisi - Kart girişinden önce göster */}
                        {balanceInfo && paymentDistribution && typeof paymentDistribution.total === 'number' && paymentDistribution.total > 0 && (
                          <div className="balance-usage-info" style={{
                            backgroundColor: '#e3f2fd',
                            border: '2px solid #2196f3',
                            borderRadius: '8px',
                            padding: '15px',
                            marginBottom: '20px'
                          }}>
                            <h4 className="text-center" style={{ color: '#1976d2', margin: '0 0 10px 0', fontSize: '16px' }}>
                              💰 Bakiye Kullanım Bilgisi
                            </h4>
                            <div style={{ fontSize: '14px', color: '#424242' }}>
                              <p style={{ margin: '5px 0' }}>
                                <strong>Toplam Tutar:</strong> {total ? total.toFixed(2) : '0.00'} TL
                              </p>
                              <p style={{ margin: '5px 0' }}>
                                <strong>Bakiyenizden Kullanılacak:</strong> <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>{(paymentDistribution.total || 0).toFixed(2)} TL</span>
                              </p>
                              <p style={{ margin: '5px 0' }}>
                                <strong>Kart ile Ödenecek:</strong> <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>{(total - (paymentDistribution.total || 0)).toFixed(2)} TL</span>
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="form-group">
                          <label>Kart Numarası:</label>
                          <input
                            type="text"
                            value={cardNumber}
                            onChange={handleCardNumberChange}
                            maxLength="16"
                            placeholder="Kart numaranızı giriniz"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Kart Üzerindeki Ad Soyad:</label>
                          <input
                            type="text"
                            value={cardHolderName}
                            onChange={handleCardHolderNameChange}
                            placeholder="Kartın üzerindeki ad soyad"
                            required
                            disabled
                          />
                        </div>

                        <div className="form-row d-flex flex-row gap-2">
                          <div className="small-group d-flex flex-column">
                            <label>Tarihi:</label>
                            <input
                              type="text"
                              value={expiryDate}
                              onChange={handleExpiryDateChange}
                              placeholder="AA/YY"
                              maxLength="5"
                              required
                            />
                          </div>
                          <div className="small-group d-flex flex-column">
                            <label>CVC:</label>
                            <input
                              type="text"
                              value={cvc}
                              onChange={handleCvcChange}
                              placeholder="CVC"
                              maxLength="3"
                              required
                            />
                          </div>
                        </div>

                        {cardNumber.length >= 8 && (
                          <div className="installment-options">
                            {(!balanceInfo || !balanceInfo.balance_covers_full_amount) && (
                              <>
                                <h3>Taksit Seçenekleri</h3>
                                <table className="installment-table">
                                  <thead>
                                    <tr>
                                      <th>Taksit Sayısı</th>
                                      <th>Aylık Ödeme</th>
                                      <th>Banka Komisyonu</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(() => {
                                      const availableInstallments = (installments && installments.length > 0)
                                        ? installments
                                        : [{ count: 1, rate: 0, totalAmount: total }];

                                      const balanceUsedTotal = balanceInfo && balanceInfo.will_use ? balanceInfo.will_use.total : 0;
                                      const amountAfterBalance = Math.max(0, total - balanceUsedTotal);

                                      return availableInstallments.map((installment) => {
                                        let displayAmount;
                                        let baseAmount = amountAfterBalance;

                                        if (hasSarrafiyeProducts && installment.count === 1) {
                                          const sarrafiyeCommission = baseAmount * 0.04;
                                          const adjustedTotal = baseAmount + sarrafiyeCommission;
                                          displayAmount = adjustedTotal.toFixed(2);
                                        } else {
                                          displayAmount = calculateMonthlyPayment(baseAmount, installment.count, installment.rate);
                                        }

                                        return (
                                          <tr key={installment.count}>
                                            <td> <label>
                                              <input
                                                type="radio"
                                                name="installment"
                                                value={installment.count}
                                                checked={selectedInstallment === installment.count}
                                                onChange={handleInstallmentChange}
                                              />
                                              {installment.count} Taksit</label>
                                            </td>
                                            <td>
                                              <span>{displayAmount} TL x {installment.count} </span>
                                            </td>
                                            <td>
                                              <span>
                                                {hasSarrafiyeProducts && installment.count === 1 ? '4' : (installment.rate || 0)} %
                                              </span>
                                            </td>
                                          </tr>
                                        );
                                      });
                                    })()}
                                  </tbody>
                                </table>

                                {hasSarrafiyeProducts && (
                                  <div className="sarrafiye-commission-info" style={{
                                    backgroundColor: '#e0f0ffff',
                                    border: '1px solid #9fcaf1ff',
                                    borderRadius: '5px',
                                    padding: '10px',
                                    marginTop: '15px',
                                    fontSize: '14px',
                                    color: '#425A8B'
                                  }}>
                                    <p style={{ margin: '0', fontWeight: 'bold' }}>
                                      ℹ️ Sarrafiye ürünlerinde 1. taksitte %4 komisyon yansıtılacaktır
                                    </p>
                                  </div>
                                )}
                              </>
                            )}

                            {errors.submit && (
                              <p className="payment-error-message">
                                {errors.submit}
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    <button type="submit" disabled={loading} className='btn btn-secondary w-100 mx-auto d-block py-3'>
                      {loading ? 'İşlem Yapılıyor...' : 'Ödeme Yap'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Payment;
